'use client';

import Link from '@eliadmin/core/src/components/link';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

import { HeroCarousel } from './hero-carousel';
import { CTALink } from '../ui/cta-link';
import { Reveal } from '../ui/reveal';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function Hero({
  references,
  subtitle,
  title,
  description,
  locale,
}: {
  references: Reference[];
  subtitle: string;
  title: string;
  description: string;
  locale: string;
}) {
  const contactId = locale === 'sk' ? 'kontaktny-formular' : 'kontaktformular';
  const iconsId = locale === 'sk' ? 'remeslo' : 'handwerk';

  const { t } = useTranslations();

  return (
    <CarouselProvider>
      <section className={cn('flex w-full items-center justify-center pl-0 md:pl-4 xl:pl-0')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-center justify-center gap-x-8 md:flex-row md:justify-between',
          )}
        >
          <div className="flex w-full flex-col items-start justify-between gap-8 px-4 py-16 sm:gap-12 md:max-w-[460px] md:gap-16 md:px-0 md:py-0 lg:min-w-[440px] lg:gap-20 xl:gap-[89px]">
            <Reveal variant="left">
              <p className="text-sm font-normal text-muted-foreground sm:text-base">{subtitle}</p>
            </Reveal>

            <div className="flex w-full flex-col items-start justify-start gap-8">
              <div className="flex w-full flex-col items-start justify-start gap-4">
                <Reveal variant="left">
                  <h1 className="relative z-[2] flex flex-wrap items-start justify-start text-4xl font-bold tracking-tighter sm:text-5xl lg:text-[56px] lg:leading-[1.1]">
                    {title}
                  </h1>
                </Reveal>
                <Reveal variant="left" width="fit-content">
                  <p className="w-full max-w-[306px] text-sm text-muted-foreground sm:text-base sm:leading-normal">
                    {description}
                  </p>
                </Reveal>
              </div>
              <Reveal variant="left" width="fit-content">
                <Button asChild>
                  <Link prefetch={false} href={`#${contactId}`}>
                    {t('text3523')}
                  </Link>
                </Button>
              </Reveal>
            </div>
            <Reveal variant="bottom" width="fit-content">
              <CTALink title={t('text2404')} url={`#${iconsId}`} />
            </Reveal>
          </div>
          <Reveal variant="right">
            <HeroCarousel references={references} />
          </Reveal>
        </div>
      </section>
    </CarouselProvider>
  );
}
